import http, { toBearerAuth } from './httpClient';
import {
  Workflow,
  Application,
  UserSettings,
  UserInfo,
  IAMUserInfo,
  Banner,
  UserMapping,
  UserAppViewSettings,
  IAdoptEaseConfig,
  IAdoptEaseUsage,
  UserLocale,
  IChannelMessagingApp,
  IAhaPilotConfig
} from '../types';
//import { OKTA_ME_URLS } from '../config/okta';
import { getOktaMeUrls } from '../config/okta';
import { APP_ENV, REACT_APP_VANITY_URL } from '../constants';
import { transformUserProfileResponse } from './userProfileTransformer';


import { UserDetailsResponse, CampaignsResponse } from './Interfaces/IUserDetailsResponse';
import { INotification } from '../components/Notifications/Notification';
import { iAppCategory } from '../components/UnifyAdministration/Blocks/AdminSideSheetDetails';
import { v4 as uuidv4 } from 'uuid';
import { insertNewRelicLog } from './newRelicApi';
import { CustomApps, UserCard } from '../components/UserCards/UserCards';
import { IActiityUser, IUserActivity } from '../components/UniPulse/interface';
import { IFeatureAccess } from '../components/DealerAdmin/DealerAdminInterface';
import { IUserIPRestrictionEntity,IPTemplate } from '../components/DealerAdmin/IPRestrictionInterface';
import { IEvent } from '../hooks/useAudit';

export function isTokenValid() {
  const expiryTime = JSON.parse(localStorage.getItem('okta-token-storage') ?? '{}')
    .accessToken
    ?.claims
    ?.exp;
  const expiryTimeMilliseconds = expiryTime * 1000;
  const expiryDate = new Date(expiryTimeMilliseconds);
  const currentDate = new Date();
  return currentDate < expiryDate;
};

export const fetchWorkflows = async (token: string, enterpriseId: string, isSessionAvailableOnVanity: boolean) => {
  if (!isTokenValid()) {
    return;
  }
  const traceId = uuidv4();
  const userLocale = localStorage.getItem('userLocale') || 'en_US'; 
  insertNewRelicLog("fetchWorkflows", traceId, token);
  const res = await http.get<Workflow[]>(`/getWorkflows/${enterpriseId}?vanity=${isSessionAvailableOnVanity}`, {
    headers: {
      Authorization: toBearerAuth(token),
      "x-trace-id": traceId,
      "Accept-Language": userLocale

    },
  });
  return res.data;
};

export const fetchApplications = async (token: string, enterpriseId: string, isSessionAvailableOnVanity: boolean) => {
  if (!isTokenValid()) {
    const storedErrorCount = localStorage.getItem('tokenInvalidError');
    if (!storedErrorCount || storedErrorCount === "0") {
      localStorage.setItem('tokenInvalidError', "1");
      window.location.reload();
    }
    return;
  }
  const userLocale = localStorage.getItem('userLocale') || 'en_US'; 
  localStorage.setItem('tokenInvalidError', "0");
  const traceId = uuidv4();
  insertNewRelicLog("fetchApplications", traceId, token);
  const res = await http
    .get<Application[]>(`/getApplications/${enterpriseId}?vanity=${isSessionAvailableOnVanity}`, {
      headers: {
        Authorization: toBearerAuth(token),
        "x-trace-id": traceId,
        'Accept-Language': userLocale
      },
    })
    .then(function (data) {
      return data.data;
    })
    .catch(function (error) {
      return error.response.data
        ? { error: error.response.data }
        : [{ error: error.message }];
    });
  return res;
};

export const fetchAllApplications = async (token: string) => {
  if (!isTokenValid()) {
    return;
  }
  const traceId = uuidv4();
  insertNewRelicLog("fetchAllApplications", traceId, token);
  const res = await http
    .get<Application[]>(`/applications`, {
      headers: {
        Authorization: toBearerAuth(token),
        "x-trace-id": traceId,
      },
    })
    .then(function (data) {
      return data.data;
    })
    .catch(function (error) {
      return error.response.data
        ? { error: error.response.data }
        : [{ error: error.message }];
    });
  return res;
};

export const fetchAllWorkflows = async (token: string) => {
  if (!isTokenValid()) {
    return;
  }
  const traceId = uuidv4();
  insertNewRelicLog("fetchAllWorkflows", traceId, token);
  const res = await http
    .get<Application[]>(`/workflows`, {
      headers: {
        Authorization: toBearerAuth(token),
        "x-trace-id": traceId,
      },
    })
    .then(function (data) {
      return data.data;
    })
    .catch(function (error) {
      return error.response.data
        ? { error: error.response.data }
        : [{ error: error.message }];
    });
  return res;
};

export const updateApplications = async (
  id: string,
  isFavorite: boolean,
  token: string
) => {
  const traceId = uuidv4();
  insertNewRelicLog("updateApplications", traceId, token);
  const res = await http.put(
    '/favoriteApplication',
    { appId: id, isFavorite },
    { headers: { Authorization: toBearerAuth(token), 'x-trace-id': traceId } }
  );
  return res.data;
};

export const updateDefaultApplication = async (
  id: string,
  isDefault: boolean,
  token: string
) => {
  const traceId = uuidv4();
  insertNewRelicLog("updateDefaultApplication", traceId, token);
  const res = await http.patch(
    '/updateDefaultApplication',
    { appId: id, isDefault},
    { headers: { Authorization: toBearerAuth(token), 'x-trace-id': traceId } }
  );
  return res.data;
};

export const updateDefaultWorkflow = async (
  id: string,
  isDefault: boolean,
  token: string
) => {
  const traceId = uuidv4();
  insertNewRelicLog("updateDefaultWorkflow", traceId, token);
  const res = await http.patch(
    '/updateDefaultWorkflow',
    { workflowId: id, isDefault },
    { headers: { Authorization: toBearerAuth(token), 'x-trace-id': traceId } }
  );
  return res.data;
};

export const updateWorkflows = async (
  id: string,
  appId: string,
  isFavorite: boolean,
  token: string
) => {
  const traceId = uuidv4();
  insertNewRelicLog("updateWorkflows", traceId, token);
  const res = await http.put(
    '/favoriteWorkflow',
    {
      appId,
      workflowId: id,
      isFavorite,
    },
    { headers: { Authorization: toBearerAuth(token), 'x-trace-id': traceId } }
  );
  return res.data;
};

export const fetchProfile = async (setIsSessionAvailableOnVanity: (isSessionAvailableOnVanity: boolean) => void, vanityDomainFlag: boolean, token:string, enableIdpProxy?: boolean) => {
  try {
    if (vanityDomainFlag) {
      const resVanity = await http.get<UserInfo>(REACT_APP_VANITY_URL, {
        withCredentials: true,
      });
      console.log("Session available on Vanity domain");
      setIsSessionAvailableOnVanity(true);
      return resVanity.data;
    }
    else {
      console.log("Session available on Okta domain");
      return fetchOktaProfile(token, enableIdpProxy)
    }
  }
  catch (error) {
    console.error("Error occurred while checking session availablity on Vanity Domain: ", JSON.stringify(error));
    const res = fetchOktaProfile(token, enableIdpProxy)
    return res;
  }
};

const fetchOktaProfile = async (token: string, enableIdpProxy?: boolean) => {
  if (!isTokenValid()) {
    return;
  }
  
  const enableIdpProxyFlagObj = (enableIdpProxy ?? false) as boolean;
  const oktaMeUrlsConfig = getOktaMeUrls(enableIdpProxyFlagObj);
  const meUrlDynamic = oktaMeUrlsConfig[APP_ENV as keyof typeof oktaMeUrlsConfig];

  let res: any = null;


  console.log("Idp-Proxy Flag value is : ", enableIdpProxyFlagObj);
  console.log("Invoking /me URL -> : ", meUrlDynamic);

  if(enableIdpProxyFlagObj) {
  res = await http.get<UserInfo>(meUrlDynamic, {
    headers: { Authorization: toBearerAuth(token)}
  });
  const data = res.data;
  return transformUserProfileResponse(data, enableIdpProxyFlagObj);
  } else {
  res = await http.get<UserInfo>(meUrlDynamic, {
    withCredentials: true,
  });
  const data = res.data;
  return transformUserProfileResponse(data, enableIdpProxyFlagObj);
  }
};

export const fetchIAMProfile = async (token: string) => {
  if (!isTokenValid()) {
    return;
  }
  const traceId = uuidv4();
  insertNewRelicLog("fetchIAMProfile", traceId, token);
  const res = await http.get<IAMUserInfo>('/getUserProfile', {
    headers: { Authorization: toBearerAuth(token), 'x-trace-id': traceId },
  });
  return res.data;
};

export const fetchSettings = async (token: string) => {
  if (!isTokenValid()) {
    return;
  }
  const traceId = uuidv4();
  insertNewRelicLog("fetchSettings", traceId, token);
  const res = await http.get<UserSettings>('/getUserPreferences', {
    headers: { Authorization: toBearerAuth(token), 'x-trace-id': traceId },
  });
  return res.data;
};

export const fetchUnifyProfile = async (token: string) => {
  if (!isTokenValid()) {
    return;
  }
  const traceId = uuidv4();
  insertNewRelicLog("fetchUnifyProfile", traceId, token);
  const res = await http.get<UserMapping>('/getUnifyUserProfile', {
    headers: { Authorization: toBearerAuth(token), 'x-trace-id': traceId },
  });
  return res.data;
};

export const updateSettings = async (
  updatedSettings: UserSettings,
  token: string
) => {
  const traceId = uuidv4();
  insertNewRelicLog("updateSettings", traceId, token);
  const res = await http.put<UserSettings>(
    '/updateUserPreferences',
    updatedSettings,
    { headers: { Authorization: toBearerAuth(token), 'x-trace-id': traceId } }
  );
  return res.data;
};

export const updateAppViewSettings = async (
  updatedSettings: UserAppViewSettings,
  token: string
) => {

  const traceId = uuidv4();
  insertNewRelicLog("updateAppViewSettings", traceId, token);
  const res = await http.put<UserAppViewSettings>(
    '/updateUserAppView',
    updatedSettings,
    { headers: { Authorization: toBearerAuth(token), 'x-trace-id': traceId } }
  );
  return res.data;
};

export const updateUserLocaleSettings = async (
  updatedLocale: UserLocale,
  token: string
) => {

  const traceId = uuidv4();
  insertNewRelicLog("updateUserLocaleSettings", traceId, token);
  const res = await http.patch<UserLocale>(
    '/updateUserLocale',
    {"userProfileUpdate" : updatedLocale},
    { headers: { Authorization: toBearerAuth(token), 'x-trace-id': traceId } }
  );
  return res.data;
};

export const fetchBanner = async (token: string) => {
  if (!isTokenValid()) {
    return;
  }
  const traceId = uuidv4();
  insertNewRelicLog("fetchBanner", traceId, token);
  const res = await http.get<Banner[]>('/getBanner', {
    headers: { Authorization: toBearerAuth(token), 'x-trace-id': traceId },
  });
  return res.data;
};


export const captureEvent = async (event: IEvent, token: string) => {
  if (!isTokenValid()) {
    return;
  }
  const traceId = uuidv4();
  insertNewRelicLog("captureEvent", traceId, token);
  const res = await http.post(
    '/captureEvent',
    { event },
    { headers: { Authorization: toBearerAuth(token), 'x-trace-id': traceId } }
  );
  return res.data;
};

export const fetchUrlResponse = async (url: string, token: string) => {
  if (!isTokenValid()) {
    return;
  }
  const traceId = uuidv4();
  insertNewRelicLog("fetchUrlResponse", traceId, token);
  const res = await http.post(
    '/getUrlResponse',
    { url },
    { headers: { Authorization: toBearerAuth(token), 'x-trace-id': traceId } }
  );
  return res.data;
};

export const fetchCNumbers = async (token: string) => {
  if (!isTokenValid()) {
    return;
  }
  const traceId = uuidv4();
  insertNewRelicLog("fetchCNumbers", traceId, token);
  const response = await http.get('/getLinkedDMS', {
    headers: {
      Authorization: toBearerAuth(token)
      , 'x-trace-id': traceId
    },
  });
  return response.data;
};

export const fetchDMSBoxMapping = async (token: string, enterpriseId: string) => {
  if (!isTokenValid()) {
    return;
  }
  const traceId = uuidv4();
  insertNewRelicLog("fetchDMSBoxMapping", traceId, token);
  const response = await http.get(`/getDMSBoxMapping`, {
    headers: {
      Authorization: toBearerAuth(token),
      'x-trace-id': traceId,
      'x-enterprise-id': enterpriseId
    },
  });
  return response.data;
};

/**
 * Common DMS UI BFF API function calls
 */

export const fetchEnterpriseUserDetails = async (
  token: string
): Promise<UserDetailsResponse> => {
  const traceId = uuidv4();
  insertNewRelicLog("fetchEnterpriseUserDetails", traceId, token);
  const res = await http.get<UserDetailsResponse>('/getUserDetails', {
    headers: { Authorization: toBearerAuth(token), 'x-trace-id': traceId },
  });
  return res.data;
};

/**
 * Notification calls
 */

export const fetchNotifications = async (token: string) => {
  if (!isTokenValid()) {
    return;
  }
  const traceId = uuidv4();
  insertNewRelicLog("fetchNotifications", traceId, token);
  const res = await http.get<INotification[]>('/getMessages', {
    headers: {
      Authorization: toBearerAuth(token), 'x-trace-id': traceId
    },
  });
  return res.data;
};

export const fetchApplicationCategory = async (token: string) => {
  if (!isTokenValid()) {
    return;
  }
  const traceId = uuidv4();
  insertNewRelicLog("fetchApplicationCategory", traceId, token);
  const res = await http.get<iAppCategory>('/applicationCategories', {
    headers: {
      Authorization: toBearerAuth(token), 'x-trace-id': traceId
    },
  });
  return res.data;
};

export const updateUserReadMessage = async (
  messageId: string,
  token: string,
  read: boolean = true
) => {
  const traceId = uuidv4();
  insertNewRelicLog("updateUserReadMessage", traceId, token);
  const res = await http.put(
    '/updateUserReadMessage',
    { messageId, read },
    { headers: { Authorization: toBearerAuth(token), 'x-trace-id': traceId } }
  );
  return res.data;
};

export const updateAllUserReadMessages = async (
  messageIds: string[],
  token: string
) => {
  const traceId = uuidv4();
  insertNewRelicLog("updateAllUserReadMessages", traceId, token);
  const res = await http.put(
    '/updateAllUserReadMessages',
    { messageIds },
    { headers: { Authorization: toBearerAuth(token), 'x-trace-id': traceId } }
  );
  return res.data;
};

export const deleteUserMessage = async (
  messageId: string,
  token: string
) => {
  const traceId = uuidv4();
  insertNewRelicLog("deleteUserMessage", traceId, token);
  const res = await http.put(
    '/deleteUserMessage',
    { messageId },
    { headers: { Authorization: toBearerAuth(token), 'x-trace-id': traceId } }
  );
  return res.data;
};
export const deleteAllUserMessages = async (
  messageIds: string[],
  token: string
) => {
  const traceId = uuidv4();
  insertNewRelicLog("deleteAllUserMessages", traceId, token);
  const res = await http.put(
    '/deleteAllUserMessages',
    { messageIds },
    { headers: { Authorization: toBearerAuth(token), 'x-trace-id': traceId } }
  );
  return res.data;
};

export const getNotificationInsights = async (
  data: any,
  token: string
) => {
  const traceId = uuidv4();
  insertNewRelicLog("getNotificationInsights", traceId, token);
  const res = await http.post(
    '/getNotificationInsights',
    data,
    { headers: { Authorization: toBearerAuth(token), 'x-trace-id': traceId } }
  );
  return res.data;
};

export const updateUserLastLogin = async (
  lastLogin: Date,
  enterpriseId: string,
  storeId: string,
  permissions: string[],
  token: string
) => {
  const traceId = uuidv4();
  insertNewRelicLog("updateUserLastLogin", traceId, token);
  const res = await http.put(
    '/updateUserLastLogin',
    { lastLogin, enterpriseId, storeId ,permissions},
    { headers: { Authorization: toBearerAuth(token), 'x-trace-id': traceId } }
  );
  return res.data;
};

export const updateUserCNumber = async (
  cNumbers: string[],
  token: string
) => {
  const traceId = uuidv4();
  insertNewRelicLog("updateUserCNumber", traceId, token);
  const res = await http.put(
    '/updateUserCNumber',
    { cNumbers },
    { headers: { Authorization: toBearerAuth(token), 'x-trace-id': traceId } }
  );
  return res.data;
};

export const fetchUnifyCampaigns = async (token: string, applications: string[], workflows: string[], enterpriseId: string, stores: string[]) => {
  if (!isTokenValid()) {
    return;
  }
  const traceId = uuidv4();
  const userLocale = localStorage.getItem('userLocale') || 'en_US'; 
  insertNewRelicLog("fetchUnifyCampaigns", traceId, token);
  const requestBody = {
    applications,
    workflows,
    enterpriseId,
    stores
  };
  const res = await http.post<CampaignsResponse[]>(`/getCampaignsForHome`,requestBody,
    {
    headers: {
      Authorization: toBearerAuth(token),
      'x-trace-id': traceId,
      'Accept-Language': userLocale
    },
  });
  return res.data;
};

export const fetchMeetMyCDKContactProfile = async (token: string, enterpriseId: string, storeId: string, isAdmin: boolean) => {
  if (!isTokenValid()) {
    return;
  }
  const traceId = uuidv4();
  insertNewRelicLog("fetchMeetMyCDKContactProfile", traceId, token);
  const res = await http.get<any[]>(`/getSFCDKContactDetails/${enterpriseId}/${storeId}/${isAdmin}`, {
    headers: {
      Authorization: toBearerAuth(token),
      'x-trace-id': traceId,
    },
  });
  return res.data;
};

export const fetchUserCards = async (token: string, enterpriseId?: string) => {
  if (!isTokenValid()) {
    return;
  }
  const traceId = uuidv4();
  insertNewRelicLog("fetchUserCards", traceId, token);
  const res = await http.get<CustomApps>(`/getCustomApps/${enterpriseId}`, {
    headers: {
      Authorization: toBearerAuth(token), 'x-trace-id': traceId
    },
  });
  return res.data;
};

export const fetchArchiveUserCards = async (token: string, enterpriseId: string) => {
  if (!isTokenValid()) {
    return;
  }
  const traceId = uuidv4();
  insertNewRelicLog("fetchArchiveUserCards", traceId, token);
  const res = await http.get<CustomApps>(`/history/getCustomApps/${enterpriseId}`, {
    headers: {
      Authorization: toBearerAuth(token), 'x-trace-id': traceId
    },
  });
  return res.data;
};


export const createUserCard = async (userCard: UserCard, token: string) => {
  if (!isTokenValid()) {
    return;
  }
  const traceId = uuidv4();
  insertNewRelicLog("createUserCard", traceId, token);
  const res = await http.post(
    '/createCustomApp',
    userCard,
    { headers: { Authorization: toBearerAuth(token), 'x-trace-id': traceId } }
  );
  return res.data;
};

export const updateUserCard = async (userCard: UserCard, token: string) => {
  if (!isTokenValid()) {
    return;
  }
  const traceId = uuidv4();
  insertNewRelicLog("updateCustomApp", traceId, token);
  const res = await http.patch(
    '/updateCustomApp',
    userCard,
    { headers: { Authorization: toBearerAuth(token), 'x-trace-id': traceId } }
  );
  return res.data;
};

export const deleteUserCard = async (userCard: UserCard, token: string) => {
  if (!isTokenValid()) {
    return;
  }
  const traceId = uuidv4();
  insertNewRelicLog("deleteCustomApp", traceId, token);
  const res = await http.patch(
    `/deleteCustomApp`,
    { appId: userCard.appId, enterpriseId: userCard.enterpriseId },
    { headers: { Authorization: toBearerAuth(token), 'x-trace-id': traceId } }
  );
  return res.data;
};

export interface IDepartment {
  departmentId: string;
  departmentLabel: string;
  departmentValue: string;
  departmentCodes?: string[];
}

export const fetchDepartments = async (token: string) => {
  if (!isTokenValid()) {
    return;
  }

  const traceId = uuidv4();

  insertNewRelicLog("fetchDepartments", traceId, token);

  const res = await http.get<IDepartment[]>(`/getDepartments`, {
    headers: {
      Authorization: toBearerAuth(token), 'x-trace-id': traceId
    },
  }).then((res: any) => {
    const departmentList = res.data.map((item: any) => ({
      id: item.departmentId,
      label: item.departmentLabel,
      value: item.departmentValue,
      codes: item.departmentCodes || []
    }));

    return departmentList;
  });

  return res;
}

export const fetchEncryptParams = async (token: string, browser: string) => {
  if (!isTokenValid()) {
    return;
  }
  const traceId = uuidv4();
  insertNewRelicLog("encryptParams", traceId, token);
  const res = await http.post<{ data: string }>('/encryptParams',
    {
      idToken: token,
      browser: browser
    },
    {
      headers: {
        Authorization: toBearerAuth(token), 'x-trace-id': traceId
      },
    });
  return res.data;
};

export const fetchCspUrls = async (token: string) => {
  if (!isTokenValid()) {
    return;
  }
  const traceId = uuidv4();
  insertNewRelicLog("fetchCspUrls", traceId, token);
  const res = await http.get<{ whiteListedUrls: Array<string> }>('/cspurls', {
    headers: {
      Authorization: toBearerAuth(token), 'x-trace-id': traceId
    },
  });
  return res.data;
};

/**
 * AdoptEase calls Start
 */

export const updateUserAdoptEaseUsage = async (token: string, workflowDetails: Record<string, Date>) => {
  if (!isTokenValid()) {
    return;
  }
  const traceId = uuidv4();
  insertNewRelicLog("updateUserAdoptEaseUsage", traceId, token);
  const res = await http.patch<{ data: string }>(`/adoptease/updateUserAdoptEaseUsage`,
    {
      workflow: workflowDetails,
    },
    {
      headers: {
        Authorization: toBearerAuth(token), 'x-trace-id': traceId
      },
    });
  return res.data;
};

export const fetchAdoptEaseConfig = async (token: string, adoptEaseFlag: boolean) => {
  if (!isTokenValid() || !adoptEaseFlag) {
    return;
  }
  const traceId = uuidv4();
  insertNewRelicLog("getAdoptEaseConfig", traceId, token);
  const res = await http.get<{ data: IAdoptEaseConfig[] }>(`/adoptease/getAdoptEaseConfig`,
    {
      headers: {
        Authorization: toBearerAuth(token), 'x-trace-id': traceId
      },
    });
  return res.data;
};

export const fetchAdoptEaseUsageByUser = async (token: string, adoptEaseFlag: boolean) => {
  if (!isTokenValid() || !adoptEaseFlag) {
    return;
  }
  const traceId = uuidv4();
  insertNewRelicLog("getAdoptEaseUsageByUser", traceId, token);
  const res = await http.get<{ data: IAdoptEaseUsage[] }>(`/adoptease/getAdoptEaseUsageByUser`,
    {
      headers: {
        Authorization: toBearerAuth(token), 'x-trace-id': traceId
      },
    });
  return res.data;
};

//New AdoptEase APIs

export const checkAdoptEaseApplicationStatus = async (token: string, userId: string) => {
  if (!isTokenValid()) {
    return;
  }
  const traceId = uuidv4();
  insertNewRelicLog("checkAdoptEaseApplicationStatus", traceId, token);
  const res = await http.post(`/adoptease/checkAdoptEaseApplicationStatus/${userId}`, 
     {
      oktaId: userId,
    },
    {headers: {
      Authorization: toBearerAuth(token), 'x-trace-id': traceId
    },
  });
  return res.data;
};

export const checkAdoptEaseDriveStatus = async (token: string, userId: string) => {
  if (!isTokenValid()) {
    return;
  }
  const traceId = uuidv4();
  insertNewRelicLog("checkAdoptEaseDriveStatus", traceId, token);
  const res = await http.post(`/adoptease/checkAdoptEaseDriveStatus/${userId}`, 
     {
      oktaId: userId,
    },
    {headers: {
      Authorization: toBearerAuth(token), 'x-trace-id': traceId
    },
  });
  return res.data;
};



/**
 * AdoptEase calls End
 */

export const fetchSystemHealthCards = async (token: string) => {
  if (!isTokenValid()) {
    return;
  }
  const traceId = uuidv4();
  insertNewRelicLog("fetchSystemHealthCards", traceId, token);
  const res = await http.get<CustomApps>('/getAllSystemHealth', {
    headers: {
      Authorization: toBearerAuth(token), 'x-trace-id': traceId
    },
  });
  return res.data;
};


export const fetchUserActivities = async (token: string,
  queryParams: any
) => {
  if (!isTokenValid()) {
    return;
  }
  const traceId = uuidv4();
  const { startTime, endTime, loginId, storeId, ip, enterpriseId } = queryParams
  insertNewRelicLog(`fetchUserActivities`, traceId, token);
  let endPoint = `/activitySearch?startTime=${startTime}&endTime=${endTime}`;
  if(loginId) endPoint += `&loginId=${loginId}`;
  if(ip) endPoint += `&ip=${ip}`;
  if(storeId) endPoint += `&storeId=${storeId}`;
  if(enterpriseId) endPoint += `&enterpriseId=${enterpriseId}`;
  const res = await http.get<IUserActivity[]>(endPoint, {
    headers: {
      Authorization: toBearerAuth(token), 'x-trace-id': traceId
    },
  });
  return res.data;
};

export const fetchActivitySearchAndGroupByUser = async (token: string, 
  startTime: string, 
  endTime: string, 
  storeIds: string, 
  loginId: string, 
  ip: string,
  enterpriseId:string,
  groupType:string) => {
  if (!isTokenValid()) {
    return;
  }
  const traceId = uuidv4();
  insertNewRelicLog("activitySearchAndGroupByUser", traceId, token);
  let api = '/activitySearchAndGroupByUser';
  if(groupType === 'STORE') api = '/activitySearchAndGroupByStore';
  if(groupType === 'IP') api = '/activitySearchAndGroupByIp';
  let endPoint = `${api}?startTime=${startTime}&endTime=${endTime}`;
  if(loginId) endPoint += `&loginId=${loginId}`;
  if(ip) endPoint += `&ip=${ip}`;
  if(storeIds) endPoint += `&storeId=${storeIds}`;
  if(enterpriseId) endPoint += `&enterpriseId=${enterpriseId}`;
  const res = await http.get<IActiityUser[]>(endPoint, {
    headers: {
      Authorization: toBearerAuth(token), 'x-trace-id': traceId
    },
  });
  return res.data;
}

export const fetchCMFNumber = async (token: string, enterpriseId:string, storeId:string) => {
  if (!isTokenValid()) {
    return;
  }
  const traceId = uuidv4();
  insertNewRelicLog("fetchCMFNumber", traceId, token);
  const res = await http.get<{shipCmf: string}>(`/getSFCMFDetails/${enterpriseId}/${storeId}`, {
    headers: {
      Authorization: toBearerAuth(token), 'x-trace-id': traceId
    },
  });
  return res.data;
};


export const searchFeatureAccess = async (token: string,
  enterpriseId: string,
  search: string
) => {
  if (!isTokenValid()) {
    return;
  }
  const traceId = uuidv4();
  insertNewRelicLog(`searchFeatureAccess`, traceId, token);
  let endPoint = `/searchFeatureAccessUser?enterpriseId=${enterpriseId}&search=${search}`;
  const res = await http.get<any>(endPoint, {
    headers: {
      Authorization: toBearerAuth(token), 

      'x-trace-id': traceId
    },
  });
  return res.data;
};


export const updateFeatureAccess = async (featureAccess: IFeatureAccess[], token: string) => {
  if (!isTokenValid()) {
    return;
  }
  const traceId = uuidv4();
  insertNewRelicLog("updateFeatureAccess", traceId, token);
  const res = await http.put(
    '/updateFeatureAccess',
    featureAccess,
    { headers: { Authorization: toBearerAuth(token), 'x-trace-id': traceId } }
  );
  return res.data;
};

export const getIPRestriction = async (token: string,enterpriseId: string, storeId: string) => {
    const traceId = uuidv4();
    insertNewRelicLog("getIPRestriction", traceId, token);
    const res = await http.get<Record<any, any>[]>(`/getIPRestriction/${enterpriseId}/${storeId}`, {
        headers: {
            Authorization: toBearerAuth(token), 'x-trace-id': traceId
        },
    });
    return res.data;
};

export const getIPTemplate = async (token: string,enterpriseId: string, storeId: string) => {
  const traceId = uuidv4();
  insertNewRelicLog("getIPTemplate", traceId, token);
  const res = await http.get<IPTemplate>(`/getIPTemplate/${enterpriseId}/${storeId}`, {
      headers: {
          Authorization: toBearerAuth(token), 'x-trace-id': traceId
      },
  });
  return res.data as any;
};

export const saveIPTemplate = async (token: string,enterpriseId: string, storeId: string, model: any) => {
  const traceId = uuidv4();
  const res = await http.put<Record<any, any>[]>(`/saveIPTemplate/${enterpriseId}/${storeId}`,
    { 
      id: model.id, 
      allowedIps: model.allowedIps, 
      allowedIpRange: model.allowedIpRange,
      name:model.name,
      applyByDefault:model.applyByDefault
    },
    {
      headers: {
          Authorization: toBearerAuth(token), 'x-trace-id': traceId
      },
  });
  return res.data;
};

export const saveIPRestriction = async (token: string,enterpriseId: string, storeId: string, model: any) => {
  const traceId = uuidv4();
  insertNewRelicLog("saveIPRestriction", traceId, token);
  const res = await http.put<Record<any, any>[]>(`/saveIPRestriction/${enterpriseId}/${storeId}`,
    { entityId: model.entityId, 
      allowedIps: model.allowedIps, 
      allowedIpRange: model.allowedIpRange,
      entityType:model.entityType ,
      message: model.message,
      templates: model.templates},
    {
      headers: {
          Authorization: toBearerAuth(token), 'x-trace-id': traceId
      },
  });
  return res.data;
};

export const fetchUserIPRestriction = async (token: string,enterpriseId: string, storeId: string) => {
  const traceId = uuidv4();
  insertNewRelicLog("fetchUserIPRestriction", traceId, token);
  const res = await http.get<IUserIPRestrictionEntity[]>(`/getUserIPRestriction/${enterpriseId}/${storeId}`, {
      headers: {
          Authorization: toBearerAuth(token), 'x-trace-id': traceId
      },
  });
  return res.data;
};

export const fetchExternalAudit = async (token: string,
  queryParams: any
) => {
  if (!isTokenValid()) {
    return;
  }
  const traceId = uuidv4();
  const { startTime, endTime, loginId, storeId, ip, enterpriseId } = queryParams
  insertNewRelicLog(`fetchExternalAudit`, traceId, token);
  let endPoint = `/externalAudit?startTime=${startTime}&endTime=${endTime}`;
  if(loginId) endPoint += `&loginId=${loginId}`;
  if(ip) endPoint += `&ip=${ip}`;
  if(storeId) endPoint += `&storeId=${storeId}`;
  if(enterpriseId) endPoint += `&enterpriseId=${enterpriseId}`;
  const res = await http.get<IUserActivity[]>(endPoint, {
    headers: {
      Authorization: toBearerAuth(token), 'x-trace-id': traceId
    },
  });
  return res.data;
};

export const fetchAllMessagingApps = async (token: string) => {
  if (!isTokenValid()) {
    return;
  }
  const traceId = uuidv4();
  insertNewRelicLog("fetchAllMessagingApps", traceId, token);
  const res = await http
    .get<IChannelMessagingApp[]>(`/getChannelMsgApps`, {
      headers: {
        Authorization: toBearerAuth(token),
        "x-trace-id": traceId,
      },
    })
    .then(function (data) {
      return data.data;
    })
    .catch(function (error) {
      return error.response.data
        ? { error: error.response.data }
        : [{ error: error.message }];
    });
  return res;
};

export const fetchAhaPilotConfig = async (token: string) => {
  if (!isTokenValid()) {
    return;
  }
  const traceId = uuidv4();
  insertNewRelicLog("fetchAhaPilotConfig", traceId, token);
  const res = await http
    .get<IAhaPilotConfig[]>(`/getAhaPilotConfig`, {
      headers: {
        Authorization: toBearerAuth(token),
        "x-trace-id": traceId,
      },
    })
    .then(function (data) {
      return data.data;
    })
    .catch(function (error) {
      return error.response.data
        ? { error: error.response.data }
        : [{ error: error.message }];
    });
  return res;
};
